/**
 * The permission catalog is the single source of truth for every gated action.
 * It is granular per-action rather than per-module because the Project Manager
 * is read-only across Requirement Analysis yet still owns the Scope Items
 * sign-off gate — a distinction a module-level flag cannot express.
 *
 * The Admin Portal's feature-flag grid renders directly from this catalog, so
 * adding an action here automatically surfaces it to administrators.
 */
export type PermissionGroup =
  | 'command-center'
  | 'projects'
  | 'requirement-analysis'
  | 'test-scripts'
  | 'administration'
  | 'assistant';

export interface PermissionDescriptor {
  readonly key: string;
  readonly group: PermissionGroup;
  readonly label: string;
  /** Non-toggleable permissions are structural and never appear in the grid. */
  readonly structural?: boolean;
}

export const PERMISSION_CATALOG = [
  { key: 'dashboard.view', group: 'command-center', label: 'View Command Center' },

  { key: 'project.view', group: 'projects', label: 'View projects', structural: true },
  {
    key: 'project.viewAll',
    group: 'projects',
    label: 'View every project (not only assigned)',
    structural: true,
  },
  { key: 'project.create', group: 'projects', label: 'Create project' },
  { key: 'project.edit', group: 'projects', label: 'Edit project details' },
  { key: 'project.delete', group: 'projects', label: 'Delete project' },
  { key: 'project.timeline.edit', group: 'projects', label: 'Edit timeline milestones' },
  { key: 'project.tasks.manage', group: 'projects', label: 'Manage tasks' },
  { key: 'project.members.manage', group: 'projects', label: 'Manage team members' },

  { key: 'analysis.view', group: 'requirement-analysis', label: 'View analyses', structural: true },
  { key: 'analysis.run', group: 'requirement-analysis', label: 'Analyze Requirements' },
  { key: 'analysis.fitgap.edit', group: 'requirement-analysis', label: 'Edit Fit-Gap rows' },
  { key: 'analysis.scope.generate', group: 'requirement-analysis', label: 'Generate Scope Items' },
  { key: 'analysis.scope.edit', group: 'requirement-analysis', label: 'Edit Scope Items' },
  { key: 'analysis.scope.rephrase', group: 'requirement-analysis', label: 'Rephrase (AI)' },
  {
    key: 'analysis.scope.approve',
    group: 'requirement-analysis',
    label: 'Approve Scope Items (sign-off gate)',
  },
  {
    key: 'analysis.scope.sendToTsg',
    group: 'requirement-analysis',
    label: 'Send to Test Script Generator',
  },
  {
    key: 'analysis.checklist.generate',
    group: 'requirement-analysis',
    label: 'Generate Development Checklist',
  },
  {
    key: 'analysis.checklist.edit',
    group: 'requirement-analysis',
    label: 'Edit Development Checklist',
  },

  {
    key: 'testScript.view',
    group: 'test-scripts',
    label: 'View generated test scripts',
    structural: true,
  },
  { key: 'testScript.generate', group: 'test-scripts', label: 'Generate test scripts' },
  { key: 'testScript.download', group: 'test-scripts', label: 'Download test scripts' },
  { key: 'testScript.email', group: 'test-scripts', label: 'Email test scripts' },

  {
    key: 'admin.portal.access',
    group: 'administration',
    label: 'Access Admin Portal',
    structural: true,
  },
  { key: 'admin.prompts.manage', group: 'administration', label: 'Author prompt templates' },
  {
    key: 'admin.prompts.publish',
    group: 'administration',
    label: 'Approve & publish prompt templates',
  },
  {
    key: 'admin.platforms.manage',
    group: 'administration',
    label: 'Manage platforms, regions & modules',
  },
  { key: 'admin.testTypes.manage', group: 'administration', label: 'Manage test types' },
  { key: 'admin.llm.manage', group: 'administration', label: 'Manage LLM API configuration' },
  { key: 'admin.users.manage', group: 'administration', label: 'Manage AD group mappings' },
  { key: 'admin.featureFlags.manage', group: 'administration', label: 'Manage role feature flags' },
  { key: 'admin.usage.view', group: 'administration', label: 'View usage & audit' },

  { key: 'assistant.use', group: 'assistant', label: 'Use the ADS Assistant' },
] as const satisfies readonly PermissionDescriptor[];

export type Permission = (typeof PERMISSION_CATALOG)[number]['key'];

export const PERMISSION_GROUP_LABEL: Readonly<Record<PermissionGroup, string>> = {
  'command-center': 'Command Center',
  projects: 'Project Module',
  'requirement-analysis': 'Requirement Analysis',
  'test-scripts': 'Test Script Generator',
  administration: 'Administration',
  assistant: 'Assistant',
};

export const ALL_PERMISSIONS: readonly Permission[] = PERMISSION_CATALOG.map((p) => p.key);

/** Permissions an administrator may toggle per role in the feature-flag grid. */
export const TOGGLEABLE_PERMISSIONS: readonly PermissionDescriptor[] = PERMISSION_CATALOG.filter(
  (p): boolean => !('structural' in p),
);
